import type { ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { GlassCard } from "@/ui";

export interface Topic {
  icon: LucideIcon;
  /** Hex colour for the icon chip and bullet dots. */
  color: string;
  title: string;
  lines: readonly ReactNode[];
  /** Optional rose "don't do this" strip pinned to the card bottom. */
  warn?: ReactNode;
}

/** Icon-chip card used by the Local & Mobile trio grids. */
export function TopicCard({ topic }: { topic: Topic }) {
  const { icon: Icon, color, title, lines, warn } = topic;
  return (
    <GlassCard className="flex h-full flex-col gap-6 p-9">
      <span
        className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-white/10"
        style={{ backgroundColor: `${color}1c` }}
      >
        <Icon className="h-10 w-10" strokeWidth={1.75} style={{ color }} />
      </span>
      <h2 className="font-display text-3xl font-semibold text-white">{title}</h2>
      <ul className="flex flex-col gap-4">
        {lines.map((line, i) => (
          <li key={i} className="flex items-start gap-4 text-2xl leading-snug text-white/85">
            <span
              className="mt-3 h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {warn && (
        <div className="mt-auto flex items-start gap-3 rounded-xl border border-accent-rose/35 bg-accent-rose/10 px-5 py-4">
          <TriangleAlert className="mt-1 h-6 w-6 shrink-0 text-accent-rose" strokeWidth={2} />
          <span className="text-xl font-medium leading-snug text-accent-rose">{warn}</span>
        </div>
      )}
    </GlassCard>
  );
}
